import { useState } from 'react';
import type { Role } from '../types';
import SafeIcon from './SafeIcon';

declare const __BUILD_ID__: string | undefined;
declare const __BUILD_TIME__: string | undefined;

function compileTimeBuildIdIfTheHostSetOne() {
  return typeof __BUILD_ID__ !== 'undefined' && __BUILD_ID__ !== 'unknown'
    ? __BUILD_ID__
    : undefined;
}

function bundleHashThatChangesWithEveryBuild() {
  return document
    .querySelector<HTMLScriptElement>('script[type="module"][src]')
    ?.src.match(/index-([\w-]+)\.js/)?.[1];
}

function buildLabel() {
  const id = compileTimeBuildIdIfTheHostSetOne() ?? bundleHashThatChangesWithEveryBuild() ?? 'dev';
  const time =
    typeof __BUILD_TIME__ !== 'undefined'
      ? new Date(__BUILD_TIME__).toLocaleString(undefined, {
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '';
  return time ? `${id} · ${time}` : id;
}

const roleLabels: Record<Role, string> = {
  owner: 'roleOwner',
  contributor: 'roleContributor',
  agency: 'roleAgency',
  admin: 'roleAdmin',
};

export default function PrototypeControls({
  role,
  setRole,
  mobile,
  setMobile,
  labels,
  onReset,
  onNewClient,
}: {
  role: Role;
  setRole: (role: Role) => void;
  mobile: boolean;
  setMobile: (value: boolean) => void;
  labels: Record<string, string>;
  onReset: () => void;
  onNewClient?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const roles: Role[] = ['owner', 'contributor', 'agency', 'admin'];
  return (
    <div className={`prototype-drawer ${open ? 'open' : ''}`}>
      <button
        className="prototype-tab"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls="prototype-panel"
        aria-label={labels['Prototype controls']}
      >
        <SafeIcon name="FiSliders" size={14} />
        <span>{labels['Prototype']}</span>
      </button>
      {open && (
        <div className="prototype-control" id="prototype-panel">
          <span>{labels.prototype}</span>
          <select
            className="select-field"
            value={role}
            onChange={(event) => setRole(event.target.value as Role)}
          >
            {roles.map((roleOption) => (
              <option key={roleOption} value={roleOption}>
                {labels[roleLabels[roleOption]]}
              </option>
            ))}
          </select>
          <button
            className={`button small ${mobile ? 'primary' : ''}`}
            onClick={() => setMobile(!mobile)}
          >
            <SafeIcon name="FiSmartphone" size={14} />
            {labels.mobile}
          </button>
          <button className="button small" onClick={onReset}>
            <SafeIcon name="FiRotateCcw" size={14} />
            {labels['Reset demo']}
          </button>
          {onNewClient && (
            <button
              className="button small"
              onClick={() => {
                setOpen(false);
                onNewClient();
              }}
            >
              <SafeIcon name="FiUserPlus" size={14} />
              {labels['Start as a new client']}
            </button>
          )}
          <span className="build-id" title={labels['Build being tested']}>
            {labels['Build']} {buildLabel()}
          </span>
          <button
            className="icon-button"
            onClick={() => setOpen(false)}
            aria-label={labels['Close']}
          >
            <SafeIcon name="FiX" size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
